import { Box, Grid, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getErrorCode, getErrorStatusCode } from "../../api/api-response";
import { PomPlatformLogo } from "../../components/Primitives";
import { removeNullAndUndefined } from "../../utils/filter.util";
import { IGroupContract } from "../../domain/group/group-contract";
import { IPaginationInputMeta, IPaginationMeta } from "../../domain/pagination/pagination-meta";
import { useGroupDetailContext } from "../../pages/groups/group-detail.provider";
import {
  useGetGroupContracts,
} from "../../repositories/queries/group/get-group-contracts.query";
import { SharedQueryKeys } from "../../repositories/queries/query-keys";
import { getFormattedContractSubtitle, getFormattedContractTitle } from "../../utils/contract.util";
import { GroupContractFormModal } from "../group/group-contract-modal/group-contract-form-modal.component";
import { GroupDetailHeader } from "../../pages/groups/components/group-detail-header.component";
import { AppTable, ITableRow } from "../tables/app-table.component";

const useStyles = makeStyles()(() => ({
  contractIcon: {
    height: 40,
    width: 50,
  },
}));

interface IGroupContractsTable {
  readOnly: boolean;
}

interface IGroupContractsTableData {
  pomContract?: IGroupContract;
}

export const GroupContractsTable: FunctionComponent<IGroupContractsTable> = (props) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { readOnly } = props;
  const { group } = useGroupDetailContext();
  const [paginationInputMeta, setPaginationInputMeta] = useState<IPaginationInputMeta>({
    page: 0,
    pageSize: 5,
  });
  const [pagination, setPagination] = useState<IPaginationMeta>({
    page: 0,
    pageSize: 5,
    numberOfPages: 0,
    numberOfRecords: 0,
  });
  const { enqueueSnackbar } = useSnackbar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<IGroupContractsTableData | undefined>();

  const handleOnLoadError = useCallback((err: unknown) => {
    const errorMsg = getErrorCode(err);
    const errorCode = getErrorStatusCode(err);
    enqueueSnackbar(t("general.error_occurred", { errorCode, errorMsg }), { variant: "error" });
  }, [enqueueSnackbar, t]);

  const { isLoading, refetch, data, error } = useGetGroupContracts(
    group.erpNumber,
    paginationInputMeta,
  );

  const queryClient = useQueryClient();

  const groupContracts = useMemo(() => {
    if (!data) return undefined;
    const contracts: IGroupContractsTableData[] = [];
    data.pomContracts.forEach((contract) => contracts.push({ pomContract: contract }));
    return contracts;
  }, [data]);

  useEffect(() => {
    if (data) {
      setPagination(data.meta);
      queryClient.invalidateQueries({ queryKey: [SharedQueryKeys.GetGroupForDetails, group.erpNumber] });
    }
  }, [data, group.erpNumber, queryClient]);

  // Handle error with useEffect in v5
  useEffect(() => {
    if (error) {
      handleOnLoadError(error);
    }
  }, [error, handleOnLoadError]);

  const getContractLabelComponent = (contract: IGroupContract) => {
    return (
      <Grid container direction="column">
        <Grid item>
          <Typography variant="body1">{getFormattedContractTitle(contract)}</Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1">{getFormattedContractSubtitle(contract)}</Typography>
        </Grid>
      </Grid>
    );
  };

  const getMappedEntries = (): ITableRow[] => {
    if (!groupContracts) return [];

    return groupContracts
      .map((contract) => {
        let entry: ITableRow | undefined = undefined;

        if (contract.pomContract) {
          entry = {
            id: contract.pomContract.contractNumber,
            labelComponent: getContractLabelComponent(contract.pomContract),
            isActive: dayjs().isBetween(contract.pomContract.startDate, contract.pomContract.endDate ?? "2999-01-01"),
            iconComponent: <PomPlatformLogo className={classes.contractIcon} />,
          };
        }

        return entry;
      })
      .filter(removeNullAndUndefined);
  };

  const handleSaved = async () => {
    setSelectedContract(undefined);
    setIsModalOpen(false);
    await refetch();
  };

  return (
    <Box>
      {isModalOpen && (
        <GroupContractFormModal
          key={`${selectedContract?.pomContract?.contractNumber}`}
          open={isModalOpen}
          onCancel={() => {
            setSelectedContract(undefined);
            setIsModalOpen(false);
          }}
          onSaved={handleSaved}
          baseContractInitialValues={selectedContract?.pomContract}
          pomContractInitialValues={selectedContract?.pomContract}
          groupType={group.type}
          readOnly={readOnly}
        />
      )}

      <GroupDetailHeader>
        <Grid container direction="row" justifyContent="space-between">
          <Grid item>
            <Typography variant="h1">{t("basedata.groups.contractsTable.contracts")}</Typography>
          </Grid>
        </Grid>
      </GroupDetailHeader>

      <AppTable
        entries={getMappedEntries()}
        paginationInputMeta={paginationInputMeta}
        setPaginationInputMeta={setPaginationInputMeta}
        numberOfRecords={pagination.numberOfRecords ?? 0}
        onRowClicked={(tableRow) => {
          const selectedPomContract = groupContracts?.find((c) => c.pomContract?.contractNumber === tableRow.id);

          setSelectedContract(selectedPomContract);
          setIsModalOpen(true);
        }}
        isLoading={isLoading}
      ></AppTable>
    </Box>
  );
};
